import { parseDateValue, useFormatDate } from '@ben/common/date';
import CloseLineIcon from '@ben/icons/close-line';
import DateInput from '@ben/orion/date-input';
import IconButton from '@ben/orion/icon-button';
import Input from '@ben/orion/input';
import Select from '@ben/orion/select';
import { TypographyVariant } from '@ben/orion/typography';
import type { DateInputRangeValue } from '@ben/orion/date-input';
import { useCallback } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { LIMIT_TYPE_OPTIONS, PERIOD_TYPE_OPTIONS } from './constant';
import { FormContainer, FormRow, Spacer, StyledTypography } from './styles';
import type { RestrictionFormPropsWithSetValue } from './types';

const StepSizeLimitForm = ({
  index,
  control,
  setValue,
  restrictionSetPath,
  onRemove,
}: RestrictionFormPropsWithSetValue) => {
  const formatDate = useFormatDate();
  const basePath = `${restrictionSetPath}.stepSizeLimits.${index}`;

  const periodType = useWatch({
    control,
    name: `${basePath}.periodType`,
  }) as string | undefined;

  const customPeriodStart = useWatch({
    control,
    name: `${basePath}.customPeriodStart`,
  }) as string | undefined;

  const customPeriodEnd = useWatch({
    control,
    name: `${basePath}.customPeriodEnd`,
  }) as string | undefined;

  const dateRangeValue: DateInputRangeValue = [
    customPeriodStart ? parseDateValue(customPeriodStart) : null,
    customPeriodEnd ? parseDateValue(customPeriodEnd) : null,
  ];

  const handleDateRangeChange = useCallback(
    (value: DateInputRangeValue) => {
      if (value) {
        const [startDate, endDate] = value;
        setValue(
          `${basePath}.customPeriodStart`,
          startDate
            ? formatDate(startDate, { formatString: 'yyyy-MM-dd' })
            : undefined
        );
        setValue(
          `${basePath}.customPeriodEnd`,
          endDate
            ? formatDate(endDate, { formatString: 'yyyy-MM-dd' })
            : undefined
        );
      }
    },
    [setValue, basePath, formatDate]
  );

  return (
    <FormContainer>
      <FormRow>
        <StyledTypography variant={TypographyVariant.bodyMd1}>
          #{index + 1} :
        </StyledTypography>
        <Controller
          control={control}
          name={`${basePath}.limitType`}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              value={field.value}
              data={LIMIT_TYPE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              onChange={field.onChange}
              width="130px"
            />
          )}
        />
        <StyledTypography variant={TypographyVariant.bodyMd1}>
          must be in
        </StyledTypography>
        <Controller
          control={control}
          name={`${basePath}.maxStepSize`}
          rules={{ required: true, min: 0 }}
          render={({ field }) => (
            <Input
              {...field}
              name="Max step size"
              type="number"
              showLabel={false}
              width="60px"
              min={0}
            />
          )}
        />
        <StyledTypography variant={TypographyVariant.bodyMd1}>
          step during
        </StyledTypography>
        <Controller
          control={control}
          name={`${basePath}.periodType`}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              value={field.value}
              data={PERIOD_TYPE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              onChange={field.onChange}
              width="200px"
            />
          )}
        />
        <Spacer />
        <IconButton
          icon={CloseLineIcon}
          size="sm"
          onClick={onRemove}
          aria-label="Remove restriction"
          variant="action"
        />
      </FormRow>

      {periodType === 'custom' && (
        <DateInput<'range'>
          type="range"
          label="Custom period"
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          width="350px"
        />
      )}
    </FormContainer>
  );
};

export default StepSizeLimitForm;
